import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {state} from '@bitstillery/mithril'
import {$t, api} from '@bitstillery/common/app'
import {Stepper} from '@bitstillery/common/components/portal'
import {classes} from '@bitstillery/common/lib/utils'

import type {SalesOrderWarehouseStatusResponse} from '@bitstillery/common/api/fact2server'

interface WarehouseStepper {
    _direction: () => string
    options: {icon: string; title: () => string}[]
    selection: number
}

interface WarehouseStatusData {
    outbound_stepper: WarehouseStepper
    release_stepper: WarehouseStepper
    /** Empty until the order's warehouse status has loaded. */
    status: Partial<SalesOrderWarehouseStatusResponse>
    title: string
}

interface WarehouseStatusAttrs {
    className?: string
    sales_order_artkey: number
}

export class WarehouseStatus extends MithrilComponent<WarehouseStatusAttrs> {
    data = state<WarehouseStatusData>(
        {
            outbound_stepper: {
                _direction: () => 'horizontal',
                options: [
                    {
                        icon: 'warehouse',
                        title: () => $t('order.details.warehouse_status.tip.notified'),
                    },
                    {
                        icon: 'cartPlus',
                        title: () => $t('order.details.warehouse_status.tip.picking'),
                    },
                    {
                        icon: 'cartCheck',
                        title: () => $t('order.details.warehouse_status.tip.inspection'),
                    },
                    {
                        icon: 'pallet_deals',
                        title: () => $t('order.details.warehouse_status.tip.loading'),
                    },
                    {
                        icon: 'truck',
                        title: () => $t('order.details.warehouse_status.tip.fullfilled'),
                    },
                ],
                selection: 0,
            },
            release_stepper: {
                _direction: () => 'horizontal',
                options: [
                    {
                        icon: 'warehouse',
                        title: () => $t('order.details.warehouse_status.tip.notified'),
                    },
                    {
                        icon: 'truck',
                        title: () => $t('order.details.warehouse_status.tip.fullfilled'),
                    },
                ],
                selection: 0,
            },
            status: {},
            title: '',
        },
        'warehouse-status-data',
    )

    override async oncreate(vnode: m.Vnode<WarehouseStatusAttrs>) {
        const {result, success} = await api.get(`portal/orders/${vnode.attrs.sales_order_artkey}/warehouse-status`)
        if (!success) {
            return
        }
        this.data.status = result
    }

    view(vnode: m.Vnode<WarehouseStatusAttrs>) {
        // No EDI information available:
        if (!this.data.status.is_release_outbound && !this.data.status.is_outbound) {
            this.data.title = $t('order.details.warehouse_status.no_edi')
        }

        const children: m.Children = [
            <div className='header'>
                <div className='title'>{$t('order.details.warehouse_status.title')}</div>
                <div className='sub-title'>{this.data.title}</div>
            </div>,
        ]

        // Create a release-outbound status flow:
        if (this.data.status.is_release_outbound) {
            children.push(<Stepper model={this.data.release_stepper} options={this.data.release_stepper.options} />)
            if (this.data.status.warehouse_done) {
                this.data.title = $t('order.details.warehouse_status.sub_title.fullfilled')
                this.data.release_stepper.selection = 1
            } else if (this.data.status.warehouse_notified) {
                this.data.title = $t('order.details.warehouse_status.sub_title.notified')
                this.data.release_stepper.selection = 0
            }
            // Create a outbound status flow:
        } else if (this.data.status.is_outbound) {
            children.push(<Stepper model={this.data.outbound_stepper} options={this.data.outbound_stepper.options} />)
            if (this.data.status.warehouse_done) {
                this.data.outbound_stepper.selection = 4
                this.data.title = $t('order.details.warehouse_status.sub_title.fullfilled')
            } else if (this.data.status.loading_finished) {
                this.data.outbound_stepper.selection = 3
                this.data.title = $t('order.details.warehouse_status.sub_title.loading_ready')
            } else if (this.data.status.loading_started) {
                this.data.outbound_stepper.selection = 3
                this.data.title = $t('order.details.warehouse_status.sub_title.loading')
            } else if (this.data.status.inspection_finished) {
                this.data.outbound_stepper.selection = 2
                this.data.title = $t('order.details.warehouse_status.sub_title.inspection_ready')
            } else if (this.data.status.inspection_started) {
                this.data.outbound_stepper.selection = 2
                this.data.title = $t('order.details.warehouse_status.sub_title.inspection')
            } else if (this.data.status.picking_finished) {
                this.data.title = $t('order.details.warehouse_status.sub_title.picking_ready')
                this.data.outbound_stepper.selection = 1
            } else if (this.data.status.picking_started) {
                this.data.title = $t('order.details.warehouse_status.sub_title.picking')
                this.data.outbound_stepper.selection = 1
            } else if (this.data.status.warehouse_notified) {
                this.data.title = $t('order.details.warehouse_status.sub_title.notified')
                this.data.outbound_stepper.selection = 0
            }
        }

        return <div className={classes('c-warehouse-status', vnode.attrs.className)}>{children}</div>
    }
}
